import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { Areas, Point, Polygon, LogicalSpace } from '@thive/fddm-types';
import { Element } from '@thive/canvas';

import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { calcOrientedLoadCarriersBoundingBox } from '@/modules/common/helpers/loadCarrier';
import { NumUtils } from '@/modules/common/utils';
import { logger } from '@common/logging/logger';
import { isDrawableToolId } from '@/modules/toolbar/guards';
import { assertNever } from '@/helpers/utils';
import { useLoadCarrierStore } from '@/modules/loadCarriers/store/useLoadCarrierStore';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useAppStore } from '@modules/app/store/useAppStore';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';

import { buildAreaFromElement } from '../helpers/buildAreaFromElement';
import { RectangleUtils } from '../helpers/RectangleUtils';
import { AtLeast2, isAtLeast3, minItemsArrayUtils } from '../helpers/minItems';
import {
  buildGenericObstacle,
  buildWall,
  getObstacleInitializationHeight,
} from '../helpers/obstacleBuilders';
import { getMinimumSize, getMinimumSizeByToolId } from '../helpers/elementSize';
import { pushAreaToItsCollection } from '../helpers';
import { getName } from '../helpers/name';
import { useFloorplanDesignStore } from '../store/useFloorplanDesignStore';

export const useNewShape = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbarStore();
  const fddmSvc: IFddmService = useMemo(
    () => FddmServiceFactory.getInstance().createFddmService(),
    [],
  );

  const newShape = useCallback(
    async (element: Element) => {
      const { selectedToolId } = useAppStore.getState();
      const {
        getPreference,
        setup: { loadTypes },
      } = useFloorplanDesignStore.getState();
      const enabledLoadTypeIds = loadTypes.map((lt) => lt.id);

      // take the first enabled vehicle and load carrier type as the default one to support
      const enabledLoadCarrierId = enabledLoadTypeIds.at(0);
      const enabledVehicleIds = useFloorplanDesignStore.getState().getVehicleTypeIds();
      const preferredVehicleId = getPreference('area.vehicleTypeId');
      const enabledVehicleId =
        preferredVehicleId && enabledVehicleIds.includes(preferredVehicleId)
          ? preferredVehicleId
          : enabledVehicleIds.at(0);

      if (!isDrawableToolId(selectedToolId)) return;

      if (!element || !enabledLoadCarrierId || !enabledVehicleId) return;

      switch (selectedToolId) {
        case 'intake':
        case 'storage':
        case 'delivery':
        case 'process':
        case 'charging':
        case 'parking':
        case 'highway':
        case 'extraction':
        case 'insertion':
        case 'manual':
        case 'racking':
          {
            if (element.type !== 'Rect') {
              // TODO: FDDM: add typesafe config map to lookup type in fddm and canvas and stuff
              logger.error(
                'Expected a AREA shape to be represented as a Rect element in the canvas',
              );
              return;
            }
            if (!element.size) {
              // TODO: CANVAS: canvas should ensure that size is defined on element with type Rect
              logger.error('Can not create a AREA. element.size is undefined.');
              return;
            }
            if (!element.position) {
              // TODO: CANVAS: canvas should ensure that position is defined on element with type Rect
              logger.error('Can not create a AREA. element.position is undefined.');
              return;
            }
            if (!element.rotation) {
              // TODO: CANVAS: canvas should ensure that rotation is defined on element with type Rect
              logger.error('Can not create a AREA. element.rotation is undefined.');
              return;
            }

            // call add facade instead
            const supportedCarrier = useLoadCarrierStore
              .getState()
              .allLoadCarriers.getByIds(enabledLoadTypeIds)
              .at(0);
            if (!supportedCarrier) {
              logger.error(
                "Couldn't determine a supported loadCarrier for a new shape. Aborting new shape creation.",
              );

              return;
            }

            // Size validation
            const loadCarrierSize = calcOrientedLoadCarriersBoundingBox(
              supportedCarrier ? [supportedCarrier] : [],
            );
            const vehicle = useVehicleStore
              .getState()
              .vehicleDetails.getByIds(useFloorplanDesignStore.getState().getVehicleTypeIds())
              .at(0);
            // loadCarrier is stored in meters; element.size and getMinimumSize work in mm.
            const loadCarrierSizeMm = {
              width: NumUtils.metersToMmRounded(loadCarrierSize.width),
              length: NumUtils.metersToMmRounded(loadCarrierSize.length),
            };
            const minSize = getMinimumSize(
              element.size,
              selectedToolId,
              vehicle,
              loadCarrierSizeMm,
            );

            const widthIsToSmall = element.size.x < minSize.width;
            const heightIsToSmall = element.size.y < minSize.height;

            if (widthIsToSmall || heightIsToSmall) {
              showSnackbar(t('errors:area.too_small_full_message'));
              if (widthIsToSmall) element.size.x = minSize.width;
              if (heightIsToSmall) element.size.y = minSize.height;
            }

            const newArea = buildAreaFromElement(
              element,
              selectedToolId,
              enabledVehicleId,
              enabledLoadCarrierId,
              loadCarrierSize.width,
              loadCarrierSize.length,
            );

            if (!newArea) return;

            const areas: Areas = {
              rackingAreas: [],
              highwayAreas: [],
              singularStationAreas: [],
              stationaryAreas: [],
              manualAreas: [],
            };

            pushAreaToItsCollection(areas, newArea);
            await fddmSvc.addAreas(areas);
            const selectedIds = [newArea.id];
            const appSvc = AppServiceFactory.getInstance().createAppService();
            CanvasServiceFactory.getInstance().createCanvasService().setSelection(selectedIds);
            appSvc.setSelectedIds(selectedIds);
          }
          break;
        case 'highway_angled': {
          if (element.type !== 'LinePath') {
            // TODO: FDDM: add typesafe config map to lookup type in fddm and canvas
            logger.error('Expected HIGHWAY_ANGLED to be represented by a LinePath in the canvas');
            return;
          }
          if (!element.points) {
            // TODO: CANVAS: canvas should ensure that points is defined on element with type LinePath
            logger.error('Can not create an HIGHWAY_ANGLED. canvasElement.points is undefined.');
            return;
          }
          if (element.points.length < 2) {
            // TODO: CANVAS: canvas should ensure that points is defined on element with type LinePath
            logger.error(
              'Can not create an HIGHWAY_ANGLED. Expected the canvas element top have at least 2 points',
            );
            return;
          }

          // call add facade instead
          const supportedCarrier = useLoadCarrierStore
            .getState()
            .allLoadCarriers.getByIds(enabledLoadTypeIds)
            .at(0);
          if (!supportedCarrier) {
            logger.error(
              "Couldn't determine a supported loadCarrier for a new shape. Aborting new shape creation.",
            );

            return;
          }
          // check shape length requirement
          const loadCarrierSize = calcOrientedLoadCarriersBoundingBox(
            supportedCarrier ? [supportedCarrier] : [],
          );
          const minLength = getMinimumSizeByToolId(selectedToolId);
          const someSegmentIsToShort = element.points
            .map((point, index, points) => {
              if (index === points.length - 1) return false;
              return point.distanceTo(points[index + 1]) < minLength;
            })
            .some((x) => x);
          if (someSegmentIsToShort) {
            showSnackbar(t('errors:angled_highway.too_short_full_message', { minLength }));
            return;
          }

          const newArea = buildAreaFromElement(
            element,
            selectedToolId,
            enabledVehicleId,
            enabledLoadCarrierId,
            loadCarrierSize.width,
            loadCarrierSize.length,
          );

          if (!newArea) return;

          const areas: Areas = {
            rackingAreas: [],
            highwayAreas: [],
            singularStationAreas: [],
            stationaryAreas: [],
            manualAreas: [],
          };

          pushAreaToItsCollection(areas, newArea);
          await fddmSvc.addAreas(areas);
          const appSvc = AppServiceFactory.getInstance().createAppService();
          CanvasServiceFactory.getInstance().createCanvasService().setSelection([newArea.id]);
          appSvc.setSelectedIds([newArea.id]);
          break;
        }
        case 'obstacle': {
          if (element.type !== 'Rect') {
            // TODO: FDDM: add typesafe config map to lookup type in fddm and canvas and stuff
            logger.error(
              'Expected a OBSTACLE shape to be represented as a Rect element in the canvas',
            );
            return;
          }
          if (!element.size) {
            // TODO: CANVAS: canvas should ensure that size is defined on element with type Rect
            logger.error('Can not create a OBSTACLE. element.size is undefined.');
            return;
          }
          if (!element.position) {
            // TODO: CANVAS: canvas should ensure that position is defined on element with type Rect
            logger.error('Can not create a OBSTACLE. element.position is undefined.');
            return;
          }
          if (!element.rotation) {
            // TODO: CANVAS: canvas should ensure that rotation is defined on element with type Rect
            logger.error('Can not create a OBSTACLE. element.rotation is undefined.');
            return;
          }

          // Size validation
          const minSize = getMinimumSize(element.size, selectedToolId);
          const widthIsToSmall = element.size.x < minSize.width;
          const heightIsToSmall = element.size.y < minSize.height;

          if (widthIsToSmall || heightIsToSmall) {
            showSnackbar(t('errors:area.too_small_full_message'));
            if (widthIsToSmall) element.size.x = minSize.width;
            if (heightIsToSmall) element.size.y = minSize.height;
          }

          const { position, size, rotation } = element;
          const rectangle = RectangleUtils.canvasLeftBottomRectToFDDMRect(
            position.x / 1000,
            position.y / 1000,
            size.x / 1000,
            size.y / 1000,
            rotation.z,
          );

          const appearance = getPreference('obstacle.appearance');
          const item = buildGenericObstacle(
            appearance,
            rectangle,
            getObstacleInitializationHeight(appearance),
          );

          await fddmSvc.addObstacles([item]);
          const appSvc = AppServiceFactory.getInstance().createAppService();
          CanvasServiceFactory.getInstance().createCanvasService().setSelection([item.id]);
          appSvc.setSelectedIds([item.id]);
          break;
        }
        case 'logical_space': {
          if (element.type !== 'Rect') {
            // TODO: FDDM: add typesafe config map to lookup type in fddm and canvas and stuff
            logger.error(
              'Expected a LOGICAL SPACE shape to be represented as a RectCentered element in the canvas',
            );
            return;
          }
          if (!element.size) {
            // TODO: CANVAS: canvas should ensure that size is defined on element with type RectCentered
            logger.error('Can not create a LOGICAL SPACE. element.size is undefined.');
            return;
          }
          if (!element.position) {
            // TODO: CANVAS: canvas should ensure that position is defined on element with type RectCentered
            logger.error('Can not create a LOGICAL SPACE. element.position is undefined.');
            return;
          }
          if (!element.rotation) {
            // TODO: CANVAS: canvas should ensure that rotation is defined on element with type RectCentered
            logger.error('Can not create a LOGICAL SPACE. element.rotation is undefined.');
            return;
          }

          // Size validation
          const minSize = getMinimumSize(element.size, selectedToolId);
          const widthIsToSmall = element.size.x < minSize.width;
          const heightIsToSmall = element.size.y < minSize.height;

          if (widthIsToSmall || heightIsToSmall) {
            showSnackbar(t('errors:area.too_small_full_message'));
            if (widthIsToSmall) element.size.x = minSize.width;
            if (heightIsToSmall) element.size.y = minSize.height;
          }

          const { position, size, rotation } = element;
          const rectangle = RectangleUtils.canvasLeftBottomRectToFDDMRect(
            position.x / 1000,
            position.y / 1000,
            size.x / 1000,
            size.y / 1000,
            rotation.z,
          );
          const points = RectangleUtils.rectangleToPolygon(rectangle).map((corner) => {
            const point: Point = { x: corner.x, y: corner.y };
            return point;
          });
          if (!isAtLeast3(points)) {
            logger.error(
              'Can not create a LOGICAL SPACE. Generated polygon has fewer than 3 points.',
            );
            return;
          }
          const polygon: Polygon = {
            points,
          };

          const item: LogicalSpace = {
            id: uuid(),
            name: getName(selectedToolId),
            trafficRules: [],
            polygon,
          };

          await fddmSvc.addLogicalSpaces([item]);
          const appSvc = AppServiceFactory.getInstance().createAppService();
          CanvasServiceFactory.getInstance().createCanvasService().setSelection([item.id]);
          appSvc.setSelectedIds([item.id]);
          break;
        }
        case 'wall': {
          if (element.type !== 'LinePath') {
            // TODO: FDDM: add typesafe config map to lookup type in fddm and canvas
            logger.error('Expected WALL to be represented by a LinePath in the canvas');
            return;
          }
          if (!element.points) {
            // TODO: CANVAS: canvas should ensure that points is defined on element with type LinePath
            logger.error('Can not create an WALL. canvasElement.points is undefined.');
            return;
          }
          if (element.points.length < 2) {
            // TODO: CANVAS: canvas should ensure that points is defined on element with type LinePath
            logger.error(
              'Can not create an WALL. Expected the canvas element top have at least 2 points',
            );
            return;
          }

          // check shape length requirement
          const minLength = getMinimumSizeByToolId(selectedToolId);
          const someSegmentIsToShort = element.points
            .map((point, index, points) => {
              if (index === points.length - 1) return false;
              return point.distanceTo(points[index + 1]) < minLength;
            })
            .some((x) => x);
          if (someSegmentIsToShort) {
            showSnackbar(t('errors:angled_highway.too_short_full_message', { minLength }));
            return;
          }

          const pointsWithMinAmount = element.points as AtLeast2<(typeof element.points)[number]>;

          const item = buildWall(
            minItemsArrayUtils.mapAtLeast2(pointsWithMinAmount, (point) => ({
              x: point.x / 1000,
              y: point.y / 1000,
            })),
            getPreference('wall.height'),
            getPreference('wall.thickness'),
            getPreference('obstacle.wallMaterial'),
          );

          if (!item) {
            logger.error('Could not create a wall.');
            return;
          }

          await fddmSvc.addObstacles([item]);
          const appSvc = AppServiceFactory.getInstance().createAppService();
          CanvasServiceFactory.getInstance().createCanvasService().setSelection([item.id]);
          appSvc.setSelectedIds([item.id]);
          break;
        }
        default:
          assertNever(selectedToolId);
      }
    },
    [fddmSvc, showSnackbar, t],
  );

  return { newShape };
};
